import { FormComponentBaseProps } from '@tmh-website/components/blocks/Form';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';

import { getCaptchaHlBasedOnLocale } from '@tmh-website/shared/helpers';
import FormSection from '@tmh-website/ui/FormSection';
import Input from '@tmh-website/ui/Input';
import { Breakpoint } from '@tmh-website/shared/const';
import Select from '@tmh-website/ui/Select';
import { StyledFormElements } from '@tmh-website/ui/FormElements';
import { SuccessMessage } from '@tmh-website/ui/FormResultMessages';
import Button from '@tmh-website/ui/Button';

import { useBusinessChargingSolutionsWeb2CaseForm } from './BusinessUnitChargingWeb2CaseForm.hooks';
import {
  type BusinessUnitChargingWeb2CaseFormValues,
  validationSchema,
} from './BusinessUnitChargingWeb2CaseForm.schema';
import { INITIAL_VALUES, QUESTION_OPTIONS } from './BusinessUnitChargingWeb2CaseForm.config';
import { CaptchaContainer } from './BusinessUnitChargingWeb2CaseForm.styles';
import { ComplaintFields, PaymentFields, ProductFields, ShippingDeliveryFields } from './components';
import { isStaticFormParameters } from '@tmh-website/services/form.service';

const BusinessUnitSolutionsWeb2CaseForm = ({
  formElementThemeColor,
  formParameters,
  buttonThemeColor,
  buttonHoverThemeColor,
}: FormComponentBaseProps): JSX.Element => {
  const { t } = useTranslation();
  const { locale } = useRouter();

  if (!isStaticFormParameters(formParameters)) {
    throw new Error(`Expected the 'formParameters' to be compliant with the 'StaticFormParameters' interface!`);
  }

  const { adminEmail } = formParameters.params;

  const { mutateAsync, isError, isLoading, isSuccess, fieldLabels } =
    useBusinessChargingSolutionsWeb2CaseForm(adminEmail);

  return (
    <Formik<BusinessUnitChargingWeb2CaseFormValues>
      validationSchema={validationSchema(t)}
      onSubmit={values => mutateAsync(values)}
      initialValues={INITIAL_VALUES}>
      {({ touched, errors, setFieldValue }) => {
        return (
          <Form data-cy='businessUnitSolutionsWeb2CaseForm'>
            <FormSection numberOfColumns={{ [Breakpoint.Sm]: 2 }}>
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={fieldLabels.first_name}
                name='first_name'
                autoComplete='given-name'
                maxLength={40}
                required
              />
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={fieldLabels.last_name}
                name='last_name'
                autoComplete='family-name'
                maxLength={40}
                required
              />
            </FormSection>
            <FormSection numberOfColumns={{ [Breakpoint.Sm]: 2 }}>
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={fieldLabels.postal_code}
                name='postal_code'
                autoComplete='postal-code'
                maxLength={20}
                required
              />
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={fieldLabels.street}
                name='street'
                autoComplete='address-level3'
                maxLength={80}
                required
              />
            </FormSection>
            <FormSection numberOfColumns={1}>
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={fieldLabels.city}
                name='city'
                autoComplete='address-level2'
                maxLength={80}
                required
              />
              <Field as={Input} themeColor={formElementThemeColor} label={fieldLabels.company} name='company' />
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={fieldLabels.phone}
                name='phone'
                autoComplete='tel'
                type='tel'
                maxLength={40}
                required
              />
              <Field
                as={Input}
                themeColor={formElementThemeColor}
                label={fieldLabels.email}
                name='email'
                autoComplete='email'
                type='email'
                maxLength={80}
                required
              />
              <Field
                as={Select}
                themeColor={formElementThemeColor}
                label={fieldLabels.question}
                name='question'
                options={QUESTION_OPTIONS}
                required
              />
              <PaymentFields {...{ formElementThemeColor }} />
              <ShippingDeliveryFields {...{ formElementThemeColor }} />
              <ComplaintFields {...{ formElementThemeColor }} />
              <ProductFields {...{ formElementThemeColor }} />
            </FormSection>

            <CaptchaContainer>
              <ReCAPTCHA
                hl={getCaptchaHlBasedOnLocale(locale)}
                onChange={token => setFieldValue('g-recaptcha-response', token)}
                sitekey={
                  process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? process.env.NX_NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ''
                }
                size='normal'
              />

              {touched['g-recaptcha-response'] && errors['g-recaptcha-response'] && (
                <StyledFormElements.ErrorMessage>{t('global:errorMissingCaptcha')}</StyledFormElements.ErrorMessage>
              )}
            </CaptchaContainer>

            <Button
              disabled={isLoading || isSuccess || Object.keys(errors).length > 0}
              hoverThemeColor={buttonHoverThemeColor}
              minWidth
              themeColor={buttonThemeColor}
              type='submit'>
              {t('global:send')}
            </Button>

            {isError && <StyledFormElements.ErrorMessage>{t('global:errorUnknown')}</StyledFormElements.ErrorMessage>}
            {isSuccess && <SuccessMessage>{t('global:contact.thanksMessage')}</SuccessMessage>}
          </Form>
        );
      }}
    </Formik>
  );
};

export default BusinessUnitSolutionsWeb2CaseForm;
